<template>
  <div class="col-12 ">
    <div class="mb-4 ps-3 no-print d-flex justify-content-between">
      <div>
        <button
          class="btn btn-primary btn-sm"
          @click="printExamSchedule"
          :disabled="loading || !readyForData"
        >
          <i class="fas fa-print ms-2"></i>
          {{ $t('examScheduleView.print') }}
        </button>
      </div>
      <div v-if="!isTeacher">
        <router-link
          :to="{ name: 'exams.schedule.settings', params: { classId: $route.params.classId } }"
          class="btn btn-secondary btn-sm"
          v-can="'exam_schedules.update'"
        >
          <i class="fas fa-cog ms-2"></i>
          {{ $t('examScheduleView.manageSchedule') }}
        </router-link>
      </div>
    </div>

    <div class="card mb-5 " style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px;">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-2 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">
              {{ $t('examScheduleView.title') }} - {{ className }}
            </h6>
            <div class="ps-3" v-if="examConfig.exam_type_id && examConfig.exam_date">
              <small class="text-white">
                {{ selectedExamType?.name || '' }} - {{ formatDate(examConfig.exam_date) }}
              </small>
            </div>
          </div>
        </div>
      </div>

      <div class="card-body px-0 pb-2 pe-3">
        <LoadingSpinner v-if="loading" :message="$t('examScheduleView.loading')" class="py-4" />
        <template v-else>
          <div v-if="!readyForData" class="alert alert-info mx-4 mb-4">
            <i class="fas fa-info-circle me-2"></i>
            <span v-if="isTeacher">
              {{ $t('examScheduleView.noConfigTeacher') }}
            </span>
            <span v-else>
              {{ $t('examScheduleView.noConfigAdmin') }}
            </span>
          </div>

          <div v-else>
            <div class="d-block d-md-none mx-4">
              <div v-for="day in days" :key="'mobile-'+day" class="mb-4">
                <h5 class="fw-bold text-center mb-3 ">{{ $t('examScheduleSettings.days.' + day) }}</h5>
                <div class="list-group">
                  <div
                    v-for="period in getPeriodsForDay(day)"
                    :key="'mobile-'+day+'-'+period.value"
                    class="list-group-item"
                    :class="{
                      'list-group-item-primary': hasExamSchedule(day, period.backendValue),
                      'list-group-item-secondary': period.value === $t('examScheduleSettings.break')
                    }"
                  >
                    <div class="d-flex justify-content-between">
                      <span>{{ period.label }}</span>
                      <small class="text-muted">{{ period.time }}</small>
                    </div>
                    <div v-if="period.value !== $t('examScheduleSettings.break')" class="mt-2">
                      {{ getSubjectName(day, period.backendValue) || '---' }}
                    </div>
                    <div v-else class="mt-2 text-center">
                      {{ $t('examScheduleSettings.break') }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="table-responsive p-4 d-none d-md-block" ref="printArea" id="exam-schedule-to-print">
              <table class="table table-bordered text-center">
                <thead>
                  <tr>
                    <th class="bg-light">{{ $t('examScheduleView.dayPeriod') }}</th>
                    <th
                      v-for="period in periodsWithTimes"
                      :key="period.value"
                      class="bg-light"
                    >
                      {{ period.label }}<br />
                      <small class="text-muted">{{ period.time }}</small>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="day in days" :key="day">
                    <td class="bg-light fw-bold">{{ $t('examScheduleSettings.days.' + day) }}</td>

                    <template v-if="day === 'السبت' && !saturdayHasExamSchedules">
                      <td
                        :colspan="periodsWithTimes.length"
                        class="empty-day"
                        style="height: 100px; vertical-align: middle;"
                      >
                        {{ $t('examScheduleView.noExams') }}
                      </td>
                    </template>

                    <template v-else>
                      <td
                        v-for="period in getPeriodsForDay(day)"
                        :key="period.value"
                        :class="{
                          'text-primary fw-bold': hasExamSchedule(day, period.backendValue),
                          'bg-secondary text-white': period.value === $t('examScheduleSettings.break')
                        }"
                      >
                        <template v-if="period.value === $t('examScheduleSettings.break')">
                          {{ $t('examScheduleSettings.break') }}
                        </template>
                        <template v-else>
                          <div v-if="hasExamSchedule(day, period.backendValue)">
                            <div class="subject-name mb-2">{{ getSubjectName(day, period.backendValue) }}</div>
                          </div>
                        </template>
                      </td>
                    </template>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, defineProps, watch, nextTick } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toastification";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import Cookies from "js-cookie";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";

const { t, locale } = useI18n();
const route = useRoute();
const store = useStore();
const toast = useToast();

const props = defineProps({
  classId: {
    type: [String, Number],
    required: true,
  },
  className: {
    type: String,
    required: true,
  },
});

const printArea = ref(null);
const examSchedules = ref([]);
const loading = ref(false);

const currentUser = computed(() => {
  return store.state.user || {
    role: {
      slug: Cookies.get('user_type') || 'teacher'
    }
  };
});

const isTeacher = computed(() => currentUser.value?.role?.slug === 'teacher');

const selectedExamType = computed(() =>
  store.getters['examTypes/active']?.find(t => t.id == examConfig.value.exam_type_id)
);

const scheduleConfig = ref({
  start_time: "08:00",
  period_duration: 90,
  total_periods: 4,
  break_duration: 30,
  break_after_period: 2
});

const examConfig = ref({
  exam_type_id: '',
  exam_date: ''
});

const days = [
  "السبت",
  "الأحد",
  "الاثنين",
  "الثلاثاء",
  "الأربعاء",
  "الخميس",
];

const periodsWithTimes = computed(() => {
  const periods = [];
  const startTime = new Date(`2000-01-01T${scheduleConfig.value.start_time}`);
  const periodDuration = scheduleConfig.value.period_duration;
  const breakDuration = scheduleConfig.value.break_duration;
  const totalPeriods = scheduleConfig.value.total_periods;
  const breakAfterPeriod = scheduleConfig.value.break_after_period;

  let currentTime = new Date(startTime.getTime());

  for (let i = 1; i <= totalPeriods; i++) {
    const periodEnd = new Date(currentTime.getTime() + periodDuration * 60000);

    const periodName = getPeriodName(i);
    const backendPeriodName = getBackendPeriodName(i);

    periods.push({
      value: periodName,
      backendValue: backendPeriodName,
      label: t('examScheduleSettings.periodLabel', { num: periodName }),
      time: `${formatTime(currentTime)} - ${formatTime(periodEnd)}`
    });

    if (i === breakAfterPeriod) {
      const breakStart = new Date(periodEnd.getTime());
      const breakEnd = new Date(breakStart.getTime() + breakDuration * 60000);

      periods.push({
        value: t('examScheduleSettings.break'),
        backendValue: "استراحة",
        label: t('examScheduleSettings.break'),
        time: `${formatTime(breakStart)} - ${formatTime(breakEnd)}`
      });

      currentTime = new Date(breakEnd.getTime());
    } else {
      currentTime = new Date(periodEnd.getTime());
    }
  }

  return periods;
});

const saturdayHasExamSchedules = computed(() => {
  if (!Array.isArray(examSchedules.value)) return false;
  return examSchedules.value.some(s => s.day === "السبت");
});

const getPeriodsForDay = (day) => {
  if (day === "السبت" && !saturdayHasExamSchedules.value) {
    return [];
  }
  return periodsWithTimes.value;
};

const getPeriodName = (number) => {
  return t(`examScheduleSettings.periodNames.${number}`, [
    "الأولى",
    "الثانية",
    "الثالثة",
    "الرابعة",
    "الخامسة",
    "السادسة",
    "السابعة"
  ][number-1] || number.toString());
};

const getBackendPeriodName = (number) => {
  const backendPeriodNames = {
    1: "الاولى",
    2: "الثانية",
    3: "الثالثة",
    4: "الرابعة",
    5: "الخامسة",
    6: "السادسة",
    7: "السابعة"
  };
  return backendPeriodNames[number] || number.toString();
};

const formatTime = (date) => {
  return date.toLocaleTimeString(locale.value === 'en' ? 'en-EG' : 'en-US', { hour12: false, hour: "2-digit", minute: "2-digit" });
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString(locale.value || 'en', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const readyForData = computed(() => !!examConfig.value.exam_type_id && !!examConfig.value.exam_date);

const loadScheduleSettings = () => {
  try {
    const savedWithGroup = localStorage.getItem(`exam_schedule_config_${route.params.classId}_${route.params.groupId}`);
    const savedWithoutGroup = localStorage.getItem(`exam_schedule_config_${route.params.classId}_default`);
    const saved = savedWithGroup || savedWithoutGroup;
    if (saved) {
      const savedConfig = JSON.parse(saved);
      scheduleConfig.value = { ...scheduleConfig.value, ...savedConfig };
      examConfig.value = {
        exam_type_id: savedConfig.exam_type_id || '',
        exam_date: savedConfig.exam_date || ''
      };
      if (savedConfig.groupId) {
        localStorage.setItem(`exam_schedule_config_${route.params.classId}_default`, JSON.stringify({
          ...savedConfig,
          groupId: savedConfig.groupId
        }));
      }
    }
  } catch (error) {/**/}
};

const fetchExamSchedules = async () => {
  try {
    loading.value = true;
    if (!examConfig.value.exam_type_id || !examConfig.value.exam_date) {
      examSchedules.value = [];
      return;
    }
    const savedConfig = JSON.parse(localStorage.getItem(`exam_schedule_config_${route.params.classId}_default`) || "null");
    const groupId = route.params.groupId || savedConfig?.groupId || null;
    const response = await store.dispatch("examSchedules/fetchClassExamSchedules", {
      classId: route.params.classId,
      groupId,
      exam_type_id: examConfig.value.exam_type_id,
      exam_date: examConfig.value.exam_date
    });
    examSchedules.value = Array.isArray(response?.data) ? response.data :
                          Array.isArray(response) ? response : [];
  } catch (error) {
    toast.error(t('examScheduleView.fetchError'));
    examSchedules.value = [];
  } finally {
    loading.value = false;
  }
};

const hasExamSchedule = (day, period) => {
  if (!Array.isArray(examSchedules.value)) return false;
  return examSchedules.value.some(s =>
    s.day === day &&
    (s.period === period || s.period === getBackendPeriodNameByLabel(period))
  );
};

const getBackendPeriodNameByLabel = (label) => {
  const map = {
    [t('examScheduleSettings.periodNames.1', "الأولى")]: "الاولى",
    [t('examScheduleSettings.periodNames.2', "الثانية")]: "الثانية",
    [t('examScheduleSettings.periodNames.3', "الثالثة")]: "الثالثة",
    [t('examScheduleSettings.periodNames.4', "الرابعة")]: "الرابعة",
    [t('examScheduleSettings.periodNames.5', "الخامسة")]: "الخامسة",
    [t('examScheduleSettings.periodNames.6', "السادسة")]: "السادسة",
    [t('examScheduleSettings.periodNames.7', "السابعة")]: "السابعة",
    [t('examScheduleSettings.break', "استراحة")]: "استراحة"
  };
  return map[label] || label;
};

const getSubjectName = (day, period) => {
  if (!Array.isArray(examSchedules.value)) return "";
  const schedule = examSchedules.value.find(s =>
    s.day === day &&
    (s.period === period || s.period === getBackendPeriodNameByLabel(period))
  );
  return schedule?.subject?.name || schedule?.subject_name || "";
};

const printExamSchedule = async () => {
  if (loading.value || !readyForData.value) {
    toast.warning(t('examScheduleView.waitToLoad') || 'Please wait until loading finishes.');
    return;
  }
  await nextTick();
  const wrapper = printArea.value;
  if (!wrapper) {
    toast.error(t('examScheduleView.printAreaMissing') || 'No printable content is available yet.');
    return;
  }
  const printContent = wrapper.innerHTML;
  const printWindow = window.open('', '_blank');
  printWindow.document.write(`
    <!DOCTYPE html>
    <html dir="${locale.value === 'ar' ? 'rtl' : 'ltr'}">
    <head>
      <title>${t('examScheduleView.title')} - ${props.className}</title>
      <meta charset="UTF-8">
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; }
        h2 { text-align: center; margin-bottom: 20px; color: #f39c12; }
        .exam-info { text-align: center; margin-bottom: 20px; font-size: 14px; color: #666; }
        table { width: 100%; border-collapse: collapse; margin: 0 auto; }
        th, td { padding: 8px; text-align: center; border: 1px solid #000; }
        .bg-warning { background-color: #f39c12 !important; color: white !important; }
        .bg-light { background-color: #f8f9fa !important; }
        .bg-secondary { background-color: #6c757d !important; color: white !important; }
        .text-muted { color: #6c757d !important; }
        .empty-day { background-color: #f8f9fa !important; color: #6c757d !important; font-style: italic; }
        .print-footer { text-align: left; margin-top: 20px; font-size: 12px; }
        @media print { body { padding: 0; } .no-print { display: none !important; } }
      </style>
    </head>
    <body>
      <h2>${t('examScheduleView.title')} - ${props.className}</h2>
      ${
        examConfig.value.exam_type_id && examConfig.value.exam_date
          ? `<div class="exam-info"><strong>${t('examScheduleView.examType')}:</strong> ${selectedExamType?.value || ''} | <strong>${t('examScheduleView.date')}:</strong> ${formatDate(examConfig.value.exam_date)}</div>`
          : ''
      }
      ${printContent}
      <div class="print-footer">
        ${t('examScheduleView.printedAt')} ${new Date().toLocaleString(locale.value || 'ar')}
      </div>
    </body>
    </html>
  `);
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
};

watch(() => route.fullPath, () => {
  loadScheduleSettings();
  fetchExamSchedules();
});

onMounted(async () => {
  loadScheduleSettings();
  if (!store.getters['examTypes/active']?.length) {
    try { await store.dispatch('examTypes/fetchAll'); } catch {/**/}
  }
  fetchExamSchedules();
});
</script>

<style scoped>
@media print {
  .no-print {
    display: none !important;
  }
}
</style>
